export interface StickyHeaderObserverConfig {
  isSmallWidth: boolean;
  isSharedView: boolean;
  onScreenSizeChange?: (isSmallWidth: boolean) => void;
}

export const MOBILE_BREAKPOINT = 768;

//let currentStickyHeaderHeight = 0;

/**
 * Observer class for managing sticky header positioning and calculating dynamic sticky header heights.
 * Automatically observes header elements and updates CSS variables based on screen size.
 * Includes utility methods for scrolling with sticky header offset.
 */
export class StickyHeaderObserver {
  private resizeObserver?: ResizeObserver;
  private windowResizeListener?: () => void;
  private config: StickyHeaderObserverConfig;
  private currentHeaderElement?: Element;
  private lastHeaderHeight = 0;
  private currentStickyHeaderHeight = 0;

  /**
   * Creates a new StickyHeaderObserver instance.
   * @param config - Configuration object containing screen size state and callbacks.
   */
  constructor(config: StickyHeaderObserverConfig) {
    this.config = config;
    this.setup();
  }

  /**
   * Gets the last measured header height in pixels.
   * @returns The height of the currently observed header element.
   */
  getLastHeaderHeight(): number {
    return this.lastHeaderHeight;
  }

  /**
   * Initializes the ResizeObserver and window resize listener.
   * Sets up automatic observation of header elements and screen size change detection.
   * @private
   */
  private setup() {
    this.resizeObserver = new ResizeObserver(() => {
      this.calculateStickyTop();
    });

    this.observeHeaderElements();

    this.windowResizeListener = () => {
      const wasSmall = this.config.isSmallWidth;
      const currentlySmall = window.innerWidth < MOBILE_BREAKPOINT;

      if (wasSmall !== currentlySmall) {
        this.config.isSmallWidth = currentlySmall;
        this.resizeObserver?.disconnect();
        this.observeHeaderElements();
        this.config.onScreenSizeChange?.(currentlySmall);
      }
      this.calculateStickyTop();
    };

    window.addEventListener('resize', this.windowResizeListener);
  }

  /**
   * Finds and observes the appropriate header element based on current screen size.
   * Disconnects previous observers and sets up new observation on the correct element.
   * @private
   */
  private observeHeaderElements() {
    this.currentHeaderElement = this.findHeaderElement();

    if (this.currentHeaderElement) {
      this.resizeObserver?.observe(this.currentHeaderElement);
    } else {
      this.currentStickyHeaderHeight = 0;
      document.documentElement.style.setProperty('--header-height', '0rem');
    }
  }

  /**
   * Locates the correct header element to observe based on screen size.
   * On mobile: looks for aside element's nested children.
   * On desktop: looks for .ansokan-header or .begaran-header elements. Add more as needed.
   * @returns The element to observe, or undefined if none found.
   * @private
   */
  private findHeaderElement(): Element | undefined {
    if (this.config.isSmallWidth) {
      const asideElement = document.querySelector('aside');
      if (asideElement.children[0]?.children[0] && !this.config.isSharedView) {
        return asideElement.children[0].children[0] as Element;
      } else if (this.config.isSharedView && asideElement) {
        return asideElement;
      }
    } else {
      const ansokanHeader = document.querySelector('.ansokan-header');
      const begaranHeader = document.querySelector('.begaran-header');

      return ansokanHeader || begaranHeader || undefined;
    }

    return undefined;
  }

  /**
   * Measures the current header element height and updates CSS variables if changed.
   * Uses different height measurement methods based on element type and screen size.
   * Updates the global currentStickyHeaderHeight variable and --header-height CSS property.
   * @private
   */
  private calculateStickyTop() {
    let headerHeight = 0;

    if (this.currentHeaderElement) {
      headerHeight = this.config.isSmallWidth
        ? this.currentHeaderElement.clientHeight
        : this.currentHeaderElement.getBoundingClientRect().height;
    }

    if (headerHeight !== this.lastHeaderHeight) {
      this.lastHeaderHeight = headerHeight;
      this.currentStickyHeaderHeight = headerHeight;
      const headerHeightInRem = headerHeight / 16;
      document.documentElement.style.setProperty(
        '--header-height',
        `${headerHeightInRem}rem`,
      );
    }
  }

  /**
   * Updates the screen size configuration and re-observes appropriate elements.
   * @param isSmallWidth - Whether the screen is currently in small width mode.
   */
  updateScreenSize(isSmallWidth: boolean) {
    if (this.config.isSmallWidth !== isSmallWidth) {
      this.config.isSmallWidth = isSmallWidth;
      this.resizeObserver?.disconnect();
      this.lastHeaderHeight = 0;
      this.observeHeaderElements();
    }
  }

  /**
   * Cleans up all observers and event listeners.
   * Should be called when the component is destroyed.
   */
  destroy() {
    this.resizeObserver?.disconnect();
    this.currentHeaderElement = undefined;
    if (this.windowResizeListener) {
      window.removeEventListener('resize', this.windowResizeListener);
    }
  }

  /**
   * Utility function to scroll to a specific element with an offset based on sticky header height.
   * Uses the dynamically calculated header height from StickyHeaderObserver.
   * @param selector - CSS selector for the target element to scroll to (current projektpart).
   */
  scrollToElementWithOffset(selector: string): void {
    setTimeout(() => {
      const headerElement = document.querySelector(selector) as HTMLElement;

      if (headerElement) {
        const rect = headerElement.getBoundingClientRect();
        const currentScrollY = window.pageYOffset;
        const headerTopInDocument = currentScrollY + rect.top;

        const offset = this.currentStickyHeaderHeight;
        const targetScrollY = headerTopInDocument - offset;

        window.scrollTo({
          top: Math.max(0, targetScrollY),
        });
      }
    }, 0);
  }
}
